import React, { useState } from 'react';
import { CalendarSheet } from '@/components/CalendarSheet';
import { ChevronRightFilled } from '@/components/Icons/ChevronRightFilled';
import { tv } from '@/utils/tv';

const trigger = tv({
  base: 'flex h-8 w-full min-w-[222px] items-center justify-between rounded-xl px-1 shadow-card md:h-9 transition-colors contrast:border contrast:border-border',
  variants: {
    isOpen: {
      true: 'bg-on-primary-surface',
      false: 'bg-card hover:bg-on-primary-surface',
    },
  },
});

const arrow = tv({
  base: 'flex size-5 shrink-0 items-center justify-center transition-colors',
  variants: {
    isDisabled: {
      true: 'cursor-default text-subtle-foreground',
      false: 'cursor-pointer text-primary',
    },
  },
});

interface Props {
  label: string;
  previousLabel: string;
  nextLabel: string;
  value: Date;
  minDate?: Date;
  maxDate?: Date;
  hasCalendarAbove?: boolean;
  className?: string;
  onChange: (date: Date) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onBeforeChange?: () => void;
}

export const PeriodPicker: React.FC<Props> = ({
  label,
  previousLabel,
  nextLabel,
  value,
  minDate,
  maxDate,
  hasCalendarAbove = false,
  className = 'w-fit',
  onChange,
  onPrevious,
  onNext,
  onBeforeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectDate = (date: Date) => {
    setIsOpen(false);
    onBeforeChange?.();
    onChange(date);
  };

  return (
    <div className={`relative ${className}`}>
      <div className={trigger({ isOpen })}>
        <button
          type="button"
          disabled={!onPrevious}
          aria-label={previousLabel}
          className={arrow({
            isDisabled: !onPrevious,
            className: 'rotate-180',
          })}
          onClick={() => {
            onBeforeChange?.();
            onPrevious?.();
          }}
        >
          <ChevronRightFilled className="size-4" />
        </button>
        <button
          type="button"
          className="flex-1 cursor-pointer px-1 text-caption-13 whitespace-nowrap text-primary md:text-subhead-14"
          onClick={() => setIsOpen(!isOpen)}
        >
          {label}
        </button>
        <button
          type="button"
          disabled={!onNext}
          aria-label={nextLabel}
          className={arrow({ isDisabled: !onNext })}
          onClick={() => {
            onBeforeChange?.();
            onNext?.();
          }}
        >
          <ChevronRightFilled className="size-4" />
        </button>
      </div>

      <CalendarSheet
        isOpen={isOpen}
        filledDate={value}
        minDate={minDate}
        maxDate={maxDate}
        popoverClassName={`left-0 w-[400px] ${hasCalendarAbove ? 'bottom-full mb-2' : 'top-full mt-2'}`}
        onChange={selectDate}
        onClose={() => setIsOpen(false)}
      />
    </div>
  );
};
